import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import {
  FiSearch,
  FiFilter,
  FiUsers,
  FiAward,
  FiMapPin,
  FiCalendar,
  FiChevronDown,
  FiChevronUp,
  FiExternalLink,
  FiEdit2,
  FiTrash2,
  FiLock,
  FiEye,
  FiEyeOff,
  FiLink,
  FiLayers,
  FiCheck,
  FiImage,
  FiX
} from 'react-icons/fi';
import { usersAPI, achievementsAPI, nftsAPI } from '../services/api';
import UserManagementModal from '../components/UserManagementModal';
import { useTranslation } from '../hooks/useTranslation';

// Helper function to mask wallet addresses (show first 3 and last 3 characters)
const maskWalletAddress = (address) => {
  if (!address) return '';
  if (address.length <= 6) return address;
  return `${address.substring(0, 3)}${'•'.repeat(Math.min(address.length - 6, 40))}${address.substring(address.length - 3)}`;
};

// Helper function to get explorer URL for wallet address based on format
const getWalletExplorerUrl = (address) => {
  if (!address) return '#';
  // EVM addresses (Camino, Polygon) start with 0x
  if (address.startsWith('0x')) {
    return `https://columbus.caminoscan.com/address/${address}`;
  }
  // Algorand addresses are 58 characters base32
  return `https://testnet.explorer.perawallet.app/address/${address}`;
};

const Users = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('created');
  const [filterBy, setFilterBy] = useState('all');
  const [expandedUser, setExpandedUser] = useState(null);
  const [managingUser, setManagingUser] = useState(null);
  const [visibleWallets, setVisibleWallets] = useState({}); // Track which wallet addresses are visible
  const [deletingUserId, setDeletingUserId] = useState(null); // Track which user is being deleted
  const [deleteError, setDeleteError] = useState(null); // Track delete errors
  const [deleteSuccess, setDeleteSuccess] = useState(null); // Track delete success
  const [isDeleting, setIsDeleting] = useState(false); // Track deletion in progress

  // Handle URL search parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchQuery = searchParams.get('search');
    if (searchQuery) {
      setSearchTerm(searchQuery);
    }
  }, [location.search]);

  // Fetch users with current filters
  const { data: usersData, isLoading, error } = useQuery(
    ['users', currentPage, searchTerm, sortBy, filterBy],
    async () => {
      const response = await usersAPI.getUsers({
        page: currentPage,
        limit: 20,
        search: searchTerm || undefined,
        sort: sortBy,
        filter: filterBy !== 'all' ? filterBy : undefined
      });
      return response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 30000
    }
  );

  // Debug logging
  console.log('Users Debug:', {
    isLoading,
    error: error?.message,
    usersData,
    usersCount: usersData?.users?.length,
    usersDataStructure: usersData ? Object.keys(usersData) : null
  });

  // Fetch user achievements when expanded
  const { data: userAchievements } = useQuery(
    ['user-achievements', expandedUser],
    async () => {
      if (!expandedUser) return null;
      const response = await achievementsAPI.getUserAchievements(expandedUser, { limit: 10 });
      return response.data;
    },
    { enabled: !!expandedUser }
  );

  // Fetch user NFTs from database (minted count)
  const { data: userNFTsDB } = useQuery(
    ['user-nfts-db', expandedUser],
    async () => {
      if (!expandedUser) return null;
      const response = await nftsAPI.getUserNFTsFromDB(expandedUser);
      return response.data;
    },
    { enabled: !!expandedUser }
  );

  // Fetch user NFTs from blockchain (current collection)
  const { data: userNFTsBlockchain } = useQuery(
    ['user-nfts-blockchain', expandedUser],
    async () => {
      if (!expandedUser) return null;
      const response = await nftsAPI.getUserBlockchainNFTs(expandedUser);
      return response.data;
    },
    { enabled: !!expandedUser }
  );

  const handleUserExpand = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  const toggleWalletVisibility = (userId) => {
    setVisibleWallets(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Clear notifications after 5 seconds
  useEffect(() => {
    if (deleteSuccess) {
      const timer = setTimeout(() => setDeleteSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [deleteSuccess]);

  useEffect(() => {
    if (deleteError) {
      const timer = setTimeout(() => setDeleteError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [deleteError]);

  // Handle user deletion
  const handleDeleteUser = async (userId, username) => {
    try {
      setIsDeleting(true);
      setDeleteError(null);

      const response = await usersAPI.deleteUser(userId);

      if (response.status === 200) {
        setDeleteSuccess(`User "${username}" has been successfully deleted.`);
        setDeletingUserId(null);

        // Refresh users list by resetting current page
        // This triggers a re-fetch via the useQuery hook
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Delete user error:', error);

      let errorMessage = 'Failed to delete user. Please try again.';

      if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to delete users.';
      } else if (error.response?.status === 404) {
        errorMessage = 'User not found. They may have already been deleted.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      setDeleteError(errorMessage);
      setIsDeleting(false);
    }
  };

  // Confirmation dialog for user deletion
  const DeleteConfirmationDialog = ({ userId, username, onConfirm, onCancel, isLoading }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
            <h3 className="text-lg font-semibold text-red-900">{t('cms.users.deleteUser')}</h3>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <p className="text-gray-700 mb-2">
              {t('cms.users.confirmDelete')} <span className="font-semibold text-gray-900">{username}</span>?
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('cms.users.deleteWarning')}
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              <p className="font-medium">{t('cms.users.deleteWarningDetails')}</p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3 bg-gray-50">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('cms.common.cancel')}
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span>{t('cms.users.deleting')}</span>
                </>
              ) : (
                <>
                  <FiTrash2 className="w-4 h-4" />
                  <span>{t('cms.users.deleteUser')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const UserCard = ({ user, isExpanded, onToggle }) => {
    const joinedDate = new Date(user.joinedAt).toLocaleDateString();
    const lastActive = user.lastLoginAt ? 
      new Date(user.lastLoginAt).toLocaleDateString() : 'Never';

    return (
      <div className="card hover:shadow-lg transition-shadow duration-200">
        <div className="flex items-start justify-between">
          {/* User Info */}
          <div className="flex items-start space-x-4 flex-1">
            <div className="flex-shrink-0">
              {user.avatarUrl ? (
                <img 
                  src={user.avatarUrl} 
                  alt={user.displayName}
                  className="w-12 h-12 rounded-full"
                />
              ) : (
                <div className="w-12 h-12 bg-secondary-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium">
                    {user.displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 flex-wrap">
                <h3 className="text-lg font-semibold text-gray-900">
                  {user.displayName}
                  {user.email && (
                    <span className="text-sm font-normal text-gray-500 ml-1">({user.email})</span>
                  )}
                </h3>
                {user.blockchainConnected && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                    <FiLink className="w-3 h-3 mr-1" /> Blockchain
                  </span>
                )}
              </div>

              {user.bio && (
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{user.bio}</p>
              )}

              {/* Quick Stats */}
              <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <FiAward className="w-4 h-4" />
                  <span>{t('cms.users.achievementsCount', { n: user.stats.achievementCount })}</span>
                </div>
                <div className="flex items-center space-x-1" title="NFTs minted by this user">
                  <FiLayers className="w-4 h-4" />
                  <span>{t('cms.users.nftCount', { n: user.stats.nftMintedCount || 0 })}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <FiCalendar className="w-4 h-4" />
                  <span>{t('cms.users.joined', { date: joinedDate })}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => setManagingUser(user)}
              className="p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-50 rounded-lg"
              title={t('cms.users.editUser')}
            >
              <FiEdit2 />
            </button>
            <button
              onClick={() => setDeletingUserId(user.id)}
              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
              title={t('cms.users.deleteUser')}
            >
              <FiTrash2 />
            </button>
            {!user.isActive && (
              <span className="p-2 text-orange-500" title={t('cms.users.userBanned')}>
                <FiLock />
              </span>
            )}
            <button
              onClick={() => onToggle(user.id)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
            </button>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Detailed Stats */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <FiAward className="w-4 h-4 mr-2" />
                  {t('cms.users.statistics')}
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('cms.users.achievements')}:</span>
                    <span className="font-medium">{user.stats.achievementCount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('cms.users.nftsMinted')}</span>
                    <span className="font-medium">{userNFTsDB?.totalNFTs || user.stats.nftMintedCount || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('cms.users.nftsInWallet')}</span>
                    <span className="font-medium text-primary-600">{userNFTsBlockchain?.totalNFTs || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('cms.users.lastActive')}:</span>
                    <span className="font-medium">{lastActive}</span>
                  </div>
                </div>
              </div>

              {/* Custodial Wallet */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <FiLock className="w-4 h-4 text-primary-600 mr-2" />
                  {t('cms.users.wallet')}
                </h4>
                {user.walletAddress ? (
                  <div className="space-y-2">
                    <div className="text-xs text-gray-600 mb-1">{t('cms.users.algorandAddress')}</div>
                    <div className="p-2 bg-primary-50 border border-primary-200 rounded relative">
                      <code className="text-xs font-mono text-primary-800 break-all pr-8">
                        {visibleWallets[user.id] ? user.walletAddress : maskWalletAddress(user.walletAddress)}
                      </code>
                      <button
                        type="button"
                        onClick={() => toggleWalletVisibility(user.id)}
                        className="absolute top-1.5 right-1.5 p-1 text-primary-600 hover:text-primary-800 focus:outline-none"
                        title={visibleWallets[user.id] ? "Hide address" : "Show address"}
                      >
                        {visibleWallets[user.id] ? (
                          <FiEyeOff className="w-4 h-4" />
                        ) : (
                          <FiEye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <div className="flex space-x-2 mt-2">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(user.walletAddress);
                          alert('Wallet address copied to clipboard!');
                        }}
                        className="flex-1 px-2 py-1 bg-secondary-100 text-secondary-700 rounded hover:bg-secondary-200 text-xs font-medium"
                      >
                        {t('cms.users.copyAddress')}
                      </button>
                      <a
                        href={getWalletExplorerUrl(user.walletAddress)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 px-2 py-1 bg-secondary-100 text-secondary-700 rounded hover:bg-secondary-200 text-xs font-medium text-center inline-flex items-center justify-center"
                      >
                        <FiExternalLink className="w-3 h-3 mr-1" />
                        {t('cms.users.explorer')}
                      </a>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-lg">
                    {t('cms.users.noWalletCreated')}
                  </p>
                )}
              </div>

              {/* Recent Achievements */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <FiAward className="w-4 h-4 mr-2" />
                  {t('cms.users.achievements')} ({user.stats.achievementCount})
                </h4>
                {userAchievements?.achievements?.length > 0 ? (
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {userAchievements.achievements.slice(0, 10).map((achievement) => (
                      <div key={achievement.id} className="flex items-start space-x-2 p-2 bg-gray-50 rounded-lg">
                        <div className={`w-6 h-6 rounded flex items-center justify-center text-xs bg-secondary-100`}>
                          {getAchievementIcon(achievement.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {achievement.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(achievement.completedAt).toLocaleDateString()}
                            {achievement.verification?.isVerified && (
                              <span className="ml-2 text-green-600 inline-flex items-center"><FiCheck className="w-3 h-3 mr-0.5" /> {t('cms.users.verified')}</span>
                            )}
                            {achievement.nft?.transactionId && (
                              <span className="ml-2 text-primary-600 inline-flex items-center"><FiLayers className="w-3 h-3 mr-0.5" /> NFT</span>
                            )}
                          </p>
                          <p className="text-xs text-gray-400 truncate mt-1">
                            {achievement.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-lg">
                    {t('cms.users.noAchievements')}
                  </p>
                )}
              </div>

              {/* User NFTs */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900 flex items-center">
                    <FiLayers className="w-4 h-4 text-primary-600 mr-2" />
                    {t('cms.users.nfts')} ({userNFTsBlockchain?.totalNFTs || 0})
                  </h4>
                  {(userNFTsBlockchain?.totalNFTs || 0) > 0 && (
                    <a
                      href={`/view-user-nfts?user=${user.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs text-primary-600 hover:text-primary-800 font-medium"
                    >
                      View Gallery
                      <FiExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  )}
                </div>
                <div className="text-xs text-gray-500 mb-2">
                  {userNFTsDB?.totalNFTs || 0} minted • {userNFTsBlockchain?.totalNFTs || 0} in wallet
                </div>
                {/* Show minted NFTs from database (userNFTsDB) since wallet NFTs are in master wallet */}
                {userNFTsDB?.nfts?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {userNFTsDB.nfts.slice(0, 10).map((nft) => {
                      const nftName = nft.name || 'Achievement NFT';
                      const imageUrl = nft.image || null;

                      return (
                        <a
                          key={nft.id || nft.assetId}
                          href={nft.assetUrl || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-3 p-2 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors cursor-pointer"
                        >
                          {/* NFT Image Circle */}
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={nftName}
                              className="w-10 h-10 rounded-full object-cover flex-shrink-0 border-2 border-primary-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center text-lg bg-primary-100 flex-shrink-0 border-2 border-primary-200"
                            style={{ display: imageUrl ? 'none' : 'flex' }}
                          >
                            <FiImage className="w-5 h-5 text-primary-600" />
                          </div>

                          {/* NFT Details */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {nftName}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {nft.template?.name || nft.achievement?.title || 'Achievement'}
                            </p>
                            {nft.assetId && (
                              <span className="text-xs text-primary-600 inline-flex items-center">
                                #{nft.assetId} <FiExternalLink className="w-3 h-3 ml-1" />
                              </span>
                            )}
                          </div>
                        </a>
                      );
                    })}
                    {(userNFTsDB?.totalNFTs || 0) > 10 && (
                      <p className="text-center text-xs text-gray-500 py-1">
                        +{userNFTsDB.totalNFTs - 10} more NFTs
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-4 text-center bg-primary-50 rounded-lg">
                    {t('cms.users.noNftsMinted')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getAchievementIcon = (type) => {
    const icons = {
      summit: '⛰️',
      lake: '🏞️',
      landmark: '🏰',
      swimming: '🏊',
      trail: '🥾',
      nature: '🌿',
      distance: '👟',
      route: '🗺️',
      cultural: '🏛️'
    };
    return icons[type] || '🏆';
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{t('cms.users.errorLoadingUsers')}: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Notification */}
      {deleteSuccess && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 rounded-lg shadow-lg p-4 max-w-md z-40 animate-fade-in">
          <div className="flex items-start space-x-3">
            <div className="text-green-600 mt-0.5"><FiCheck className="w-5 h-5" /></div>
            <div>
              <h3 className="font-medium text-green-900">Success</h3>
              <p className="text-sm text-green-800 mt-1">{deleteSuccess}</p>
            </div>
            <button
              onClick={() => setDeleteSuccess(null)}
              className="text-green-600 hover:text-green-800"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Error Notification */}
      {deleteError && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 rounded-lg shadow-lg p-4 max-w-md z-40 animate-fade-in">
          <div className="flex items-start space-x-3">
            <div className="text-red-600 mt-0.5"><FiX className="w-5 h-5" /></div>
            <div>
              <h3 className="font-medium text-red-900">Error</h3>
              <p className="text-sm text-red-800 mt-1">{deleteError}</p>
            </div>
            <button
              onClick={() => setDeleteError(null)}
              className="text-red-600 hover:text-red-800"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600">{t('cms.nav.users')}</h1>
          <p className="text-gray-600">{t('cms.users.subtitle')}</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('cms.common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Sort */}
          <div className="flex space-x-4">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="created">{t('cms.users.sortRecentlyJoined')}</option>
              <option value="achievements">{t('cms.users.sortMostAchievements')}</option>
              <option value="distance">{t('cms.users.sortMostDistance')}</option>
              <option value="nfts">{t('cms.users.sortMostNfts')}</option>
              <option value="last_active">{t('cms.users.sortRecentlyActive')}</option>
            </select>

            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">{t('cms.users.filterAllUsers')}</option>
              <option value="high_achievers">{t('cms.users.filterHighAchievers')}</option>
              <option value="new_users">{t('cms.users.filterNewUsers')}</option>
              <option value="nft_collectors">{t('cms.users.filterNftCollectors')}</option>
              <option value="active_adventurers">{t('cms.users.filterRecentlyActive')}</option>
            </select>
          </div>
        </div>

        {/* Results Summary */}
        {usersData?.pagination && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <FiUsers className="w-4 h-4" />
              <span>
                {t('cms.users.totalUsers')}: {((currentPage - 1) * 20) + 1}-{Math.min(currentPage * 20, usersData.pagination.totalUsers)}
                / {usersData.pagination.totalUsers}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <FiFilter className="w-4 h-4" />
              <span>{t('cms.users.filters')}: {filterBy.replace('_', ' ')}</span>
            </div>
          </div>
        )}
      </div>

      {/* Users List */}
      {isLoading ? (
        <div className="space-y-4">
          <p className="sr-only">{t('cms.common.loading')}</p>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                  <div className="flex space-x-4">
                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                    <div className="h-3 bg-gray-200 rounded w-16"></div>
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : usersData?.users?.length > 0 ? (
        <div className="space-y-4">
          {usersData.users.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              isExpanded={expandedUser === user.id}
              onToggle={handleUserExpand}
            />
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <FiUsers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">{t('cms.users.noUsersFound')}</h3>
          <p className="text-gray-500 mt-1">
            {searchTerm ?
              t('cms.users.noUsersMatchSearch', { term: searchTerm }) :
              t('cms.users.noUsersMatchFilters')
            }
          </p>
        </div>
      )}

      {/* Pagination */}
      {usersData?.pagination && usersData.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
            disabled={!usersData.pagination.hasPrev}
            className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('cms.common.previous')}
          </button>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">
              {t('cms.common.pageOf', { current: usersData.pagination.currentPage, total: usersData.pagination.totalPages })}
            </span>
          </div>

          <button
            onClick={() => setCurrentPage(page => page + 1)}
            disabled={!usersData.pagination.hasNext}
            className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('cms.common.next')}
          </button>
        </div>
      )}

      {/* User Management Modal */}
      <UserManagementModal
        user={managingUser}
        isOpen={!!managingUser}
        onClose={() => setManagingUser(null)}
      />

      {/* Delete Confirmation Dialog */}
      {deletingUserId && usersData?.users && (
        <DeleteConfirmationDialog
          userId={deletingUserId}
          username={usersData.users.find(u => u.id === deletingUserId)?.displayName || 'Unknown User'}
          onConfirm={() => {
            const user = usersData.users.find(u => u.id === deletingUserId);
            if (user) {
              handleDeleteUser(deletingUserId, user.displayName);
            }
          }}
          onCancel={() => setDeletingUserId(null)}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default Users;